/**
 * OrderUndoControl — "Return to {status}" action for the order detail.
 *
 * Renders nothing when the order isn't undoable. Otherwise a quiet outline
 * button opens a confirmation popup (reusing OrderActionConfirm with reverse
 * status chips) and POSTs the undo. The server clears the side effects of the
 * undone step (ETA / reason) and re-reserves stock for reinstated orders.
 */

import { useState } from "react";
import { useToast } from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { SellerOrderDetail } from "@/lib/api/types";
import { getOrderStatusLabel } from "../order-status";
import { getUndoTarget, getUndoKind } from "@/modules/orders/lib/order-undo";
import { useUndoOrderStatusMutation } from "@/modules/orders/services/useUndoOrderStatusMutation";
import { OrderActionConfirm } from "../OrderActionConfirm";

type Props = {
  order: SellerOrderDetail;
  storeSlug: string;
};

export function OrderUndoControl({ order, storeSlug }: Props) {
  const t = useTranslations();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const { mutateAsync: undoStatus } = useUndoOrderStatusMutation();

  const target = getUndoTarget(order);
  if (!target) return null;

  const targetLabel = getOrderStatusLabel(t, target);
  const label = t(keys.orders.undoActionLabel, { status: targetLabel });
  const kind = getUndoKind(order.status);
  const bodyText = t(
    kind === "eta"
      ? keys.orders.undoBodyEta
      : kind === "reason"
        ? keys.orders.undoBodyReason
        : keys.orders.undoBodyPlain,
    { status: targetLabel },
  );

  const run = async (): Promise<void> => {
    setSaving(true);
    try {
      await undoStatus({ storeSlug, orderId: order.id });
      toast.success(t(keys.orders.statusSavedMicro));
      setOpen(false);
    } catch (error) {
      const outOfStock =
        error instanceof Error && error.message.includes("OUT_OF_STOCK");
      toast.error(
        t(outOfStock ? keys.orders.undoOutOfStock : keys.errors.generic),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex h-[42px] min-w-[140px] flex-1 items-center justify-center gap-2 rounded-[11px] border border-[color:var(--border-strong)] bg-[color:var(--card)] px-[11px] text-[13.5px] font-semibold text-[color:var(--text-primary)] transition-colors hover:bg-[color:var(--soft)]"
      >
        <UndoIcon />
        {label}
      </button>
      <OrderActionConfirm
        isOpen={open}
        orderCode={order.orderCode}
        fromStatus={order.status}
        toStatus={target}
        bodyKey={null}
        bodyText={bodyText}
        confirmLabel={label}
        needsEta={false}
        isSubmitting={saving}
        onConfirm={() => void run()}
        onClose={() => setOpen(false)}
      />
    </>
  );
}

function UndoIcon() {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="rtl:-scale-x-100"
    >
      <path d="M3 7v6h6" />
      <path d="M3 13a9 9 0 1 0 3-7.7L3 8" />
    </svg>
  );
}
